<template>
  <div class="print-header">
    <div class="print-header__logo-container">
      <ImageMedia
        class="print-header__logo"
        testId="print-header-logo"
        :src="logoMediaObject"
        :defaultImage="defaultLogo"
        :style="{ width: `${logoUrlWidth}px` }"
      />
    </div>
    <div class="print-header__title-container">
      <p
        class="print-header__title"
        data-private
        :style="{ 'font-size': `${revFontSize}px` }"
      >
        {{ revName }}
        <span>
          <v-chip class="print-header__status-chip caption" small>
            {{ revStatus }}
          </v-chip>
          <v-icon
            v-if="isValidated && !isFinanciallyValidated"
            class="ml-3"
            color="green"
            >mdi-rocket-launch</v-icon
          >
          <v-icon v-if="isFinanciallyValidated" class="ml-3" color="green"
            >mdi-check-decagram</v-icon
          >
        </span>
      </p>
      <div class="d-flex flex-wrap flex-grow-1">
        <p class="print-header__attribute d-flex align-center" data-private>
          <v-icon class="me-1" color="blue-grey darken-3">mdi-warehouse</v-icon
          >{{ siteName }}
        </p>
        <p
          v-for="tag in tags"
          class="print-header__attribute"
          data-private
          :key="tag.id"
        >
          {{ tag.name }}
        </p>
      </div>
    </div>
    <div class="print-header__info d-flex">
      <div v-if="challengeName" class="mr-3">
        <p
          class="print-header__info-title mb-0"
          data-testid="print_challenge_title"
        >
          {{ $t('common.challenge_one') }}:
        </p>
        <p
          class="print-header__info-name"
          data-testid="print_challenge_name"
          data-private
        >
          {{ challengeName }}
        </p>
      </div>
      <div
        v-if="creationDate"
        class="mr-3"
        data-testid="print-header-creation-date"
      >
        <p class="print-header__info-title mb-0">
          {{ $t('common.creation_date') }}:
        </p>
        <p class="print-header__info-name">{{ creationDate }}</p>
      </div>
      <div v-if="completedDate" class="mr-3">
        <p class="print-header__info-title mb-0">
          {{ $t('common.completed_date') }}:
        </p>
        <p class="print-header__info-name">{{ completedDate }}</p>
      </div>

      <div v-if="implementationBy && !isFinanciallyValidated" class="mr-3">
        <p class="print-header__info-title mb-0">
          {{ $t('common.implemented_by') }}:
        </p>
        <p class="print-header__info-name">
          {{ implementationBy.displayname }} {{ implementationBy.familyname }}
        </p>
      </div>

      <div v-if="implementationBy && !isFinanciallyValidated" class="mr-3">
        <p class="print-header__info-title mb-0">
          {{ $t('rev.validation.implemented_on') }}:
        </p>
        <p class="print-header__info-name">
          {{ formatDate(implementationAt) }}
        </p>
      </div>

      <div v-if="isFinanciallyValidated" class="mr-3">
        <p class="print-header__info-title mb-0">
          {{ $t('rev.validation.validated_by') }}
        </p>
        <p class="print-header__info-name">
          {{ validationBy.displayname }} {{ validationBy.familyname }}
        </p>
      </div>

      <div v-if="isFinanciallyValidated" class="mr-3">
        <p class="print-header__info-title mb-0">
          {{ $t('rev.validation.validated_on') }}
        </p>
        <p class="print-header__info-name">
          {{ formatDate(validationAt) }}
        </p>
      </div>
    </div>
  </div>
</template>
<script>
import _ from 'lodash'

import { utils } from 'frontend-js-shared'
import ImageMedia from '@/components/ImageMedia/ImageMedia.vue'
import Logo from '@/assets/images/logo-rever.png'

export default {
  name: 'PrintHeader',

  props: {
    logoUrlWidth: {
      type: String,
      required: false,
      default: '120',
    },
    logoMediaObject: {
      type: Object,
      required: false,
    },
    defaultLogo: {
      type: String,
      required: false,
      default: Logo,
    },
    revName: {
      type: String,
      required: true,
    },
    revFontSize: {
      type: String,
      required: false,
      default: '20',
    },
    siteName: {
      type: String,
      required: true,
    },
    tags: {
      type: Array,
      required: false,
      default: () => [],
    },
    challengeName: {
      type: String,
      required: false,
    },
    completedDate: {
      type: String,
      required: false,
    },
    creationDate: {
      type: String,
      required: false,
    },
    rev: { type: Object, required: true },
    statusText: {
      type: String,
      required: true,
    },
    isValidated: {
      type: Boolean,
      required: true,
    },
    isFinanciallyValidated: {
      type: Boolean,
      required: true,
    },
    isHighlighted: {
      type: Boolean,
      required: true,
    },
    validationDate: {
      type: String,
      required: false,
    },
  },

  components: {
    ImageMedia,
  },

  computed: {
    revStatus() {
      return this.$t(
        `common.rev_condition.${_.snakeCase(
          this.rev?.revCondition ?? this.rev?.status
        )}`
      )
    },
    implementationBy() {
      return (
        this.rev?.validations__implementation__validatedBy ??
        this.rev?.validations?.implementation?.auditor ??
        false
      )
    },
    implementationAt() {
      return (
        this.rev?.validations__implementation__validatedAt ??
        this.rev?.validations?.implementation?.createdAt
      )
    },
    validationBy() {
      return (
        this.rev?.validations__financial__validatedBy ??
        this.rev?.validations?.financial?.auditor ??
        false
      )
    },
    validationAt() {
      return (
        this.rev?.validations__financial__validatedAt ??
        this.rev?.validations?.financial?.createdAt
      )
    },
  },

  methods: {
    formatDate(date, format) {
      return utils.dates.formatDate(date, format)
    },
  },
}
</script>
<style lang="scss" scoped>
.print-header {
  display: flex;
  justify-content: space-between;

  &__attributes-container {
    display: flex;
  }

  &__title {
    font-weight: bold;
    margin-bottom: 4px;
    color: $color-blue-darken2;

    &-container {
      padding-right: 0;
      padding-left: 0;
      flex-grow: 1;
    }
  }

  &__attribute {
    background-color: $color-blue-grey-lighten3 !important;
    border: 1px solid $color-blue-grey-darken1;
    border-radius: 4px;
    padding: 4px 6px;
    margin-top: 4px;
    margin-right: 8px;
    font-weight: 500;
    font-size: 12px;
    color: $color-blue-darken2;
  }

  &__info {
    text-align: right;
    min-width: 15%;

    &-title {
      font-size: 12px;
      margin-bottom: 2px;
      font-weight: bold;
    }

    &-name {
      font-size: 12px;
    }
  }

  &__attribute {
    margin-right: 8;
    font-weight: 500;
  }
  &__logo-container {
    margin-right: 16px;
  }
  &__logo {
    height: auto;
  }
  &__status-chip {
    background: $color-blue-grey-lighten2 !important;
    border: solid 1px $color-blue-grey !important;
  }
}
</style>
